// GrafanaLink — Phase 1G item 4. A small clickable badge ("see in Grafana")
// that either opens the configured Grafana panel or, if Grafana is not yet
// provisioned, opens a dialog explaining what the link will do once a URL is
// configured + offers an admin-only "Configure Grafana URL" button.
//
// Visible-but-honest: operators see the entry point and the panel name they
// will land on, even before Grafana is reachable.
import { useState } from "react";
import { Activity, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import { usePatchSettings, useSettings } from "~/lib/settings";

export interface GrafanaLinkProps {
    /** Stable id for the panel; rendered into the URL once Grafana is wired. */
    panelId: string;
    /** Human label for the metric the operator is drilling into. */
    metric: string;
    /** Title of the planned Grafana panel — surfaced in the stub dialog. */
    panelName: string;
    /** Free-form sentence answering "When will this work?". */
    eta?: string;
}

const KEY = "observability.grafana_url";

export function GrafanaLink({ panelId, metric, panelName, eta }: GrafanaLinkProps): React.ReactElement {
    const me = useCurrentUser();
    const settings = useSettings();
    const patch = usePatchSettings();
    const [open, setOpen] = useState(false);
    const [draftUrl, setDraftUrl] = useState<string>("");

    const isAdmin = (me.data?.roles ?? []).includes("admin");
    const grafanaUrl = typeof settings.data?.[KEY] === "string" ? (settings.data?.[KEY] as string) : "";
    const target = grafanaUrl ? `${grafanaUrl.replace(/\/$/, "")}/d/${panelId}` : "";

    function handleClick(e: React.MouseEvent<HTMLButtonElement>): void {
        if (target) {
            // Real link — let the anchor wrapper handle navigation.
            return;
        }
        e.preventDefault();
        setDraftUrl(grafanaUrl);
        setOpen(true);
    }

    async function saveUrl(): Promise<void> {
        try {
            await patch.mutateAsync({ [KEY]: draftUrl || null });
            toast.success("Grafana URL saved");
            setOpen(false);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to save URL");
        }
    }

    const inner = (
        <button
            type="button"
            className="chip"
            onClick={handleClick}
            title={target ? `Open ${panelName} in Grafana` : "Grafana drill-through (not yet provisioned)"}
            style={{ cursor: target ? "pointer" : "help" }}
        >
            <Activity size={12} aria-hidden /> Grafana
        </button>
    );

    return (
        <>
            {target ? (
                <a href={target} target="_blank" rel="noreferrer noopener" className="cluster-xs" style={{ textDecoration: "none" }}>
                    {inner}
                </a>
            ) : (
                inner
            )}

            {open && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Grafana drill-through stub"
                    onClick={() => setOpen(false)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 80,
                    }}
                >
                    <div
                        className="card stack"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: 520, width: "calc(100% - var(--space-6))", padding: "var(--space-5)" }}
                    >
                        <div className="stack-xs">
                            <h2 className="type-h2">Grafana is not yet provisioned</h2>
                            <p className="text-13 text-text-muted">
                                The platform isn't pointed at a Grafana instance yet. Once an admin
                                configures one, this badge will deep-link to the panel below.
                            </p>
                        </div>
                        <dl className="stack-xs" style={{ margin: 0 }}>
                            <div className="cluster-xs"><dt className="text-12 text-text-muted" style={{ minWidth: 110 }}>Metric</dt><dd className="text-13" style={{ margin: 0 }}>{metric}</dd></div>
                            <div className="cluster-xs"><dt className="text-12 text-text-muted" style={{ minWidth: 110 }}>Planned panel</dt><dd className="text-13" style={{ margin: 0 }}>{panelName}</dd></div>
                            <div className="cluster-xs"><dt className="text-12 text-text-muted" style={{ minWidth: 110 }}>Panel id</dt><dd><code className="type-mono-sm text-text-muted">{panelId}</code></dd></div>
                            {eta && (
                                <div className="cluster-xs"><dt className="text-12 text-text-muted" style={{ minWidth: 110 }}>When</dt><dd className="text-13" style={{ margin: 0 }}>{eta}</dd></div>
                            )}
                        </dl>
                        {isAdmin && (
                            <div className="stack-xs">
                                <label className="stack-xs">
                                    <span className="text-13">Grafana base URL</span>
                                    <input
                                        type="url"
                                        className="input"
                                        placeholder="https://grafana.intelroom.com"
                                        value={draftUrl}
                                        onChange={(e) => setDraftUrl(e.target.value)}
                                    />
                                </label>
                                <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
                                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn--primary btn--sm press-crush"
                                        onClick={() => void saveUrl()}
                                        disabled={patch.isPending}
                                    >
                                        <ExternalLink size={14} /> Save URL
                                    </button>
                                </div>
                            </div>
                        )}
                        {!isAdmin && (
                            <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
                                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)}>
                                    Close
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
